import type { ComponentProps, CSSProperties } from "react";
import { motion } from "framer-motion";
import type { AreaId } from "../../content/map";

type Props = {
  areaId: AreaId;
  size?: number;
  className?: string;
  /** true なら動かさずに描く（未解放ノードのシルエット用。常時アニメの描き直しを減らす） */
  still?: boolean;
};

/** アニメ指定を捨てて素の div で描く */
function StillDiv({ animate, transition, initial, style, ...rest }: ComponentProps<typeof motion.div>) {
  void animate;
  void transition;
  void initial;
  return <div {...(rest as ComponentProps<"div">)} style={style as CSSProperties} />;
}

export function AnimatedAreaIcon({ areaId, size = 52, className = "", still = false }: Props) {
  const M = still ? StillDiv : motion.div;
  switch (areaId) {
    case "airport":
      return (
        <div className={`relative flex items-center justify-center select-none ${className}`} style={{ width: size, height: size }}>
          {/* 流れる雲 */}
          <M
            animate={{ x: [-12, 12, -12], opacity: [0.6, 0.9, 0.6] }}
            transition={{ repeat: Infinity, duration: 4, ease: "easeInOut" }}
            className="absolute top-1 text-stone-300 pointer-events-none"
            style={{ fontSize: size * 0.35 }}
          >
            ☁️
          </M>
          {/* 旋回する飛行機 */}
          <M
            animate={{
              y: [-2, 3, -2],
              rotate: [-4, 5, -4],
            }}
            transition={{ repeat: Infinity, duration: 2.6, ease: "easeInOut" }}
            style={{ fontSize: size * 0.7 }}
          >
            ✈️
          </M>
        </div>
      );

    case "home":
      return (
        <div className={`relative flex items-center justify-center select-none ${className}`} style={{ width: size, height: size }}>
          {/* 煙突の煙 */}
          <M
            animate={{
              y: [0, -14],
              x: [0, 4, 8],
              opacity: [0.8, 0],
              scale: [0.5, 1.2],
            }}
            transition={{ repeat: Infinity, duration: 2.2, ease: "easeOut" }}
            className="absolute -top-1 right-3 text-stone-400 pointer-events-none"
            style={{ fontSize: size * 0.28 }}
          >
            💭
          </M>
          <M
            animate={{ scale: [1, 1.05, 1] }}
            transition={{ repeat: Infinity, duration: 3, ease: "easeInOut" }}
            style={{ fontSize: size * 0.7 }}
          >
            🏡
          </M>
        </div>
      );

    case "school":
      return (
        <div className={`relative flex items-center justify-center select-none ${className}`} style={{ width: size, height: size }}>
          {/* 揺れるチャイムの音符 */}
          <M
            animate={{
              y: [-2, -8, -2],
              opacity: [0.4, 1, 0.4],
              rotate: [-10, 10, -10],
            }}
            transition={{ repeat: Infinity, duration: 1.8, ease: "easeInOut" }}
            className="absolute -top-1.5 right-2 text-amber-500 pointer-events-none"
            style={{ fontSize: size * 0.3 }}
          >
            🔔
          </M>
          <M
            animate={{ y: [0, -2, 0] }}
            transition={{ repeat: Infinity, duration: 2.8, ease: "easeInOut" }}
            style={{ fontSize: size * 0.7 }}
          >
            🏫
          </M>
        </div>
      );

    case "shop":
      return (
        <div className={`relative flex items-center justify-center select-none ${className}`} style={{ width: size, height: size }}>
          {/* 24hネオンのきらめき */}
          <M
            animate={{ opacity: [0.4, 1, 0.4] }}
            transition={{ repeat: Infinity, duration: 1.5, ease: "easeInOut" }}
            className="absolute -top-1 left-2 text-yellow-400 pointer-events-none"
            style={{ fontSize: size * 0.25 }}
          >
            ✨
          </M>
          <M
            animate={{ scale: [1, 1.04, 1] }}
            transition={{ repeat: Infinity, duration: 2.5, ease: "easeInOut" }}
            style={{ fontSize: size * 0.7 }}
          >
            🏪
          </M>
        </div>
      );

    case "cafe":
      return (
        <div className={`relative flex items-center justify-center select-none ${className}`} style={{ width: size, height: size }}>
          {/* ゆらゆら立ち上る湯気スチーム */}
          <M
            animate={{
              y: [0, -10, -16],
              opacity: [0, 0.85, 0],
              scale: [0.6, 1, 1.2],
            }}
            transition={{ repeat: Infinity, duration: 2.0, ease: "easeOut" }}
            className="absolute -top-2 text-stone-400 pointer-events-none font-bold"
            style={{ fontSize: size * 0.3 }}
          >
            ♨️
          </M>
          <M
            animate={{ rotate: [-2, 2, -2] }}
            transition={{ repeat: Infinity, duration: 3.2, ease: "easeInOut" }}
            style={{ fontSize: size * 0.7 }}
          >
            ☕
          </M>
        </div>
      );

    case "station":
      return (
        <div className={`relative flex items-center justify-center select-none ${className}`} style={{ width: size, height: size }}>
          {/* ガタゴト揺れる電車 */}
          <M
            animate={{
              y: [0, -3, 0, -1, 0],
              x: [-1, 1, -1],
            }}
            transition={{ repeat: Infinity, duration: 1.4, ease: "linear" }}
            style={{ fontSize: size * 0.7 }}
          >
            🚃
          </M>
          {/* 点滅する信号 */}
          <M
            animate={{ opacity: [0.2, 1, 0.2] }}
            transition={{ repeat: Infinity, duration: 1.1, ease: "easeInOut" }}
            className="absolute -bottom-1 right-2 text-emerald-500 pointer-events-none"
            style={{ fontSize: size * 0.25 }}
          >
            🟢
          </M>
        </div>
      );

    case "library":
      return (
        <div className={`relative flex items-center justify-center select-none ${className}`} style={{ width: size, height: size }}>
          {/* 開く本と知恵の光 */}
          <M
            animate={{
              scale: [0.95, 1.08, 0.95],
              rotate: [-2, 2, -2],
            }}
            transition={{ repeat: Infinity, duration: 2.8, ease: "easeInOut" }}
            style={{ fontSize: size * 0.7 }}
          >
            📚
          </M>
          <M
            animate={{ y: [-1, -6, -1], opacity: [0.3, 0.9, 0.3] }}
            transition={{ repeat: Infinity, duration: 2.2, ease: "easeInOut" }}
            className="absolute -top-1.5 text-blue-400 pointer-events-none"
            style={{ fontSize: size * 0.25 }}
          >
            📖
          </M>
        </div>
      );

    case "park":
      return (
        <div className={`relative flex items-center justify-center select-none ${className}`} style={{ width: size, height: size }}>
          {/* 舞い散る花びら */}
          <M
            animate={{
              x: [-6, 8, -6],
              y: [-4, 6, -4],
              rotate: [0, 180, 360],
            }}
            transition={{ repeat: Infinity, duration: 3.5, ease: "easeInOut" }}
            className="absolute -top-2 right-1 text-pink-400 pointer-events-none"
            style={{ fontSize: size * 0.3 }}
          >
            🌸
          </M>
          <M
            animate={{ scale: [1, 1.05, 1] }}
            transition={{ repeat: Infinity, duration: 3, ease: "easeInOut" }}
            style={{ fontSize: size * 0.7 }}
          >
            ⛲
          </M>
        </div>
      );

    case "friendhouse":
      return (
        <div className={`relative flex items-center justify-center select-none ${className}`} style={{ width: size, height: size }}>
          {/* 弾むプレゼントハート */}
          <M
            animate={{
              y: [-1, -7, -1],
              scale: [0.9, 1.15, 0.9],
            }}
            transition={{ repeat: Infinity, duration: 2.0, ease: "easeInOut" }}
            className="absolute -top-2 text-rose-500 pointer-events-none"
            style={{ fontSize: size * 0.3 }}
          >
            💝
          </M>
          <M
            animate={{ rotate: [-2, 2, -2] }}
            transition={{ repeat: Infinity, duration: 2.6, ease: "easeInOut" }}
            style={{ fontSize: size * 0.7 }}
          >
            🏠
          </M>
        </div>
      );

    case "hospital":
      return (
        <div className={`relative flex items-center justify-center select-none ${className}`} style={{ width: size, height: size }}>
          {/* 心電図ハートビート */}
          <M
            animate={{
              scale: [1, 1.3, 1, 1.2, 1],
            }}
            transition={{ repeat: Infinity, repeatDelay: 1.2, duration: 0.8 }}
            className="absolute -top-1.5 right-2 text-red-500 pointer-events-none"
            style={{ fontSize: size * 0.28 }}
          >
            💗
          </M>
          <M
            animate={{ y: [0, -2, 0] }}
            transition={{ repeat: Infinity, duration: 3, ease: "easeInOut" }}
            style={{ fontSize: size * 0.7 }}
          >
            🏥
          </M>
        </div>
      );

    case "cityoffice":
      return (
        <div className={`relative flex items-center justify-center select-none ${className}`} style={{ width: size, height: size }}>
          {/* スタンプポンポン */}
          <M
            animate={{
              y: [-8, 0, -8],
              scale: [0.8, 1.1, 0.8],
            }}
            transition={{ repeat: Infinity, repeatDelay: 1.5, duration: 0.6, ease: "easeIn" }}
            className="absolute -top-2 left-2 text-indigo-600 pointer-events-none"
            style={{ fontSize: size * 0.3 }}
          >
            💮
          </M>
          <M
            animate={{ scale: [1, 1.03, 1] }}
            transition={{ repeat: Infinity, duration: 3, ease: "easeInOut" }}
            style={{ fontSize: size * 0.7 }}
          >
            🏢
          </M>
        </div>
      );

    case "workplace":
      return (
        <div className={`relative flex items-center justify-center select-none ${className}`} style={{ width: size, height: size }}>
          {/* カチコチ動く時計 */}
          <M
            animate={{ rotate: [0, 360] }}
            transition={{ repeat: Infinity, duration: 8, ease: "linear" }}
            className="absolute -top-1.5 right-2 text-amber-600 pointer-events-none"
            style={{ fontSize: size * 0.28 }}
          >
            ⏱️
          </M>
          <M
            animate={{ y: [0, -2, 0] }}
            transition={{ repeat: Infinity, duration: 2.6, ease: "easeInOut" }}
            style={{ fontSize: size * 0.7 }}
          >
            💼
          </M>
        </div>
      );

    default:
      return <div className="text-2xl select-none">📍</div>;
  }
}
